﻿/*!
    *
    * Wijmo Library 5.20251.31
    * https://developer.mescius.com/wijmo
    *
    * Copyright(c) MESCIUS inc. All rights reserved.
    *
    * Licensed under the End-User License Agreement For MESCIUS Wijmo Software.
    * us.sales@mescius.com
    * https://developer.mescius.com/wijmo/licensing
    *
    */
var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
import { VueApi, WjComponentBehavior } from '@mescius/wijmo.vue2.base';
import * as wjcChart from '@mescius/wijmo.chart';
var WjFlexChartBehavior = /** @class */ (function (_super) {
    __extends(WjFlexChartBehavior, _super);
    function WjFlexChartBehavior() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    WjFlexChartBehavior.prototype._updateControl = function (property, newValue) {
        switch (property) {
            case 'tooltipContent':
                this.control.tooltip.content = newValue;
                break;
            case 'labelContent':
                this.control.dataLabel.content = newValue;
                break;
            default:
                _super.prototype._updateControl.call(this, property, newValue);
        }
    };
    WjFlexChartBehavior.tag = 'wj-flex-chart';
    WjFlexChartBehavior.props = [
        'renderEngine',
        'isDisabled',
        'tabOrder',
        'binding',
        'footer',
        'header',
        'selectionMode',
        'palette',
        'plotMargin',
        'footerStyle',
        'headerStyle',
        'tooltipContent',
        'itemsSource',
        'bindingX',
        'interpolateNulls',
        'legendToggle',
        'symbolSize',
        'options',
        'selection',
        'itemFormatter',
        'labelContent',
        'chartType',
        'rotated',
        'stacking'
    ];
    WjFlexChartBehavior.events = [
        'initialized',
        'gotFocus',
        'lostFocus',
        'refreshing',
        'refreshed',
        'invalidInput',
        'rendering',
        'rendered',
        'itemsSourceChanging',
        'itemsSourceChanged',
        'seriesVisibilityChanged'
    ];
    WjFlexChartBehavior.changeEvents = {
        'selectionChanged': ['selection'],
    };
    WjFlexChartBehavior.classCtor = function () { return wjcChart.FlexChart; };
    return WjFlexChartBehavior;
}(WjComponentBehavior));
/**
 * Vue component for the {@link wijmo.chart.FlexChart} control.
 *
 * The <b>wj-flex-chart</b> component may contain
 * the following child components:
 * {@link wijmo.vue2.chart.analytics.WjFlexChartTrendLine}
 * , {@link wijmo.vue2.chart.analytics.WjFlexChartMovingAverage}
 * , {@link wijmo.vue2.chart.analytics.WjFlexChartYFunctionSeries}
 * , {@link wijmo.vue2.chart.analytics.WjFlexChartParametricFunctionSeries}
 * , {@link wijmo.vue2.chart.analytics.WjFlexChartWaterfall}
 * , {@link wijmo.vue2.chart.analytics.WjFlexChartBoxWhisker}
 * , {@link wijmo.vue2.chart.analytics.WjFlexChartErrorBar}
 * , {@link wijmo.vue2.chart.analytics.WjFlexChartBreakEven}
 * , {@link wijmo.vue2.chart.animation.WjFlexChartAnimation}
 * , {@link wijmo.vue2.chart.annotation.WjFlexChartAnnotationLayer}
 * , {@link wijmo.vue2.chart.interaction.WjFlexChartRangeSelector}
 * , {@link wijmo.vue2.chart.interaction.WjFlexChartGestures}
 * , {@link wijmo.vue2.chart.WjFlexChartAxis}
 * , {@link wijmo.vue2.chart.WjFlexChartLegend}
 * , {@link wijmo.vue2.chart.WjFlexChartDataLabel}
 * , {@link wijmo.vue2.chart.WjFlexChartSeries}
 * , {@link wijmo.vue2.chart.WjFlexChartLineMarker}
 * and {@link wijmo.vue2.chart.WjFlexChartPlotArea}.
 *
 * The component supports all properties and events of the pure JavaScript {@link wijmo.chart.FlexChart} control it represents.
 *
 * The component includes an <b>initialized</b> event that is raised when the control is initialized after it is added to the page.
 * You can use this event to perform further initialization in addition to setting properties in markup.
 * The signature of the handler function is the same as any other Wijmo event handlers.
 *
 * The example below shows how to instantiate and initialize a
 * {@link wijmo.chart.FlexChart} control using Vue markup:
 *
 * <pre>&lt;wj-flex-chart
 *     :items-source="data"
 *     binding-x="country"
 *     :header="props.header"
 *     :footer="props.footer"&gt;
 *
 *     &lt;wj-flex-chart-legend :position="props.legendPosition"&gt;
 *     &lt;/wj-flex-chart-legend&gt;
 *     &lt;wj-flex-chart-axis wj-property="axisX" :title="props.titleX"&gt;
 *     &lt;/wj-flex-chart-axis&gt;
 *     &lt;wj-flex-chart-axis wj-property="axisY" :title="props.titleY"&gt;
 *     &lt;/wj-flex-chart-axis&gt;
 *
 *     &lt;wj-flex-chart-series name="Sales" binding="sales"&gt;
 *     &lt;/wj-flex-chart-series&gt;
 *     &lt;wj-flex-chart-series name="Expenses" binding="expenses"&gt;
 *     &lt;/wj-flex-chart-series&gt;
 *     &lt;wj-flex-chart-series name="Downloads" binding="downloads"&gt;
 *     &lt;/wj-flex-chart-series&gt;
 * &lt;/wj-flex-chart&gt;</pre>
 *
 * The code sets the <b>itemsSource</b> property to a collection that contains the chart
 * data and the <b>bindingX</b> property to the data property that contains the chart X values.
 * It also sets the chart's <b>header</b> and <b>footer</b> properties to define titles to
 * show above and below the chart.
 *
 * The <b>wj-flex-chart-legend</b> and <b>wj-flex-chart-axis</b> components are used to
 * customize the chart's legend and axes.
 *
 * Finally, three <b>wj-flex-chart-series</b> components are used to specify the data
 * properties to be shown on the chart.
 */
export var WjFlexChart = WjFlexChartBehavior.register();
function registerV3WjFlexChart(app) {
    app.component(WjFlexChartBehavior.tag, WjFlexChart);
}
var WjFlexPieBehavior = /** @class */ (function (_super) {
    __extends(WjFlexPieBehavior, _super);
    function WjFlexPieBehavior() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    WjFlexPieBehavior.prototype._updateControl = function (property, newValue) {
        switch (property) {
            case 'tooltipContent':
                this.control.tooltip.content = newValue;
                break;
            case 'labelContent':
                this.control.dataLabel.content = newValue;
                break;
            default:
                _super.prototype._updateControl.call(this, property, newValue);
        }
    };
    WjFlexPieBehavior.tag = 'wj-flex-pie';
    WjFlexPieBehavior.props = [
        'isDisabled',
        'tabOrder',
        'binding',
        'footer',
        'header',
        'selectionMode',
        'palette',
        'plotMargin',
        'footerStyle',
        'headerStyle',
        'tooltipContent',
        'itemsSource',
        'bindingName',
        'innerRadius',
        'isAnimated',
        'offset',
        'options',
        'reversed',
        'startAngle',
        'selectedIndex',
        'selectedItemPosition',
        'selectedItemOffset',
        'itemFormatter',
        'labelContent',
        'titles',
        'chartsPerLine',
        'innerText',
        'innerTextStyle'
    ];
    WjFlexPieBehavior.events = [
        'initialized',
        'gotFocus',
        'lostFocus',
        'refreshing',
        'refreshed',
        'invalidInput',
        'rendering',
        'rendered',
        'selectionChanged',
        'itemsSourceChanging',
        'itemsSourceChanged'
    ];
    WjFlexPieBehavior.classCtor = function () { return wjcChart.FlexPie; };
    return WjFlexPieBehavior;
}(WjComponentBehavior));
/**
 * Vue component for the {@link wijmo.chart.FlexPie} control.
 *
 * The <b>wj-flex-pie</b> component may contain
 * the following child components:
 * {@link wijmo.vue2.chart.animation.WjFlexChartAnimation}
 * , {@link wijmo.vue2.chart.WjFlexChartLegend}
 * and {@link wijmo.vue2.chart.WjFlexPieDataLabel}.
 *
 * The component supports all properties and events of the pure JavaScript {@link wijmo.chart.FlexPie} control it represents.
 *
 * The component includes an <b>initialized</b> event that is raised when the control is initialized after it is added to the page.
 * You can use this event to perform further initialization in addition to setting properties in markup.
 * The signature of the handler function is the same as any other Wijmo event handlers.
 */
export var WjFlexPie = WjFlexPieBehavior.register();
function registerV3WjFlexPie(app) {
    app.component(WjFlexPieBehavior.tag, WjFlexPie);
}
var WjFlexChartAxisBehavior = /** @class */ (function (_super) {
    __extends(WjFlexChartAxisBehavior, _super);
    function WjFlexChartAxisBehavior() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    WjFlexChartAxisBehavior.tag = 'wj-flex-chart-axis';
    WjFlexChartAxisBehavior.parentProp = 'axes';
    WjFlexChartAxisBehavior.props = [
        'wjProperty',
        'axisLine',
        'format',
        'labels',
        'majorGrid',
        'majorTickMarks',
        'majorUnit',
        'max',
        'min',
        'position',
        'reversed',
        'title',
        'labelAngle',
        'minorGrid',
        'minorTickMarks',
        'minorUnit',
        'origin',
        'logBase',
        'plotArea',
        'labelAlign',
        'name',
        'overlappingLabels',
        'labelPadding',
        'itemFormatter',
        'itemsSource',
        'binding',
        'labelMin',
        'labelMax',
        'groupsOptions'
    ];
    WjFlexChartAxisBehavior.events = [
        'initialized',
        'rangeChanged'
    ];
    WjFlexChartAxisBehavior.classCtor = function () { return wjcChart.Axis; };
    return WjFlexChartAxisBehavior;
}(WjComponentBehavior));
/**
 * Vue component for the {@link wijmo.chart.Axis} class.
 *
 * The <b>wj-flex-chart-axis</b> component should be contained in
 * one of the following components:
 * {@link wijmo.vue2.chart.WjFlexChart}
 * , {@link wijmo.vue2.chart.WjFlexChartSeries}
 * , {@link wijmo.vue2.chart.finance.WjFinancialChart}
 *  or {@link wijmo.vue2.chart.finance.WjFinancialChartSeries}.
 *
 * The component supports all properties and events of the pure JavaScript {@link wijmo.chart.Axis} class it represents.
 *
 * The component includes an <b>initialized</b> event that is raised when the control is initialized after it is added to the page.
 * You can use this event to perform further initialization in addition to setting properties in markup.
 * The signature of the handler function is the same as any other Wijmo event handlers.
 */
export var WjFlexChartAxis = WjFlexChartAxisBehavior.register();
function registerV3WjFlexChartAxis(app) {
    app.component(WjFlexChartAxisBehavior.tag, WjFlexChartAxis);
}
var WjFlexChartLegendBehavior = /** @class */ (function (_super) {
    __extends(WjFlexChartLegendBehavior, _super);
    function WjFlexChartLegendBehavior() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    WjFlexChartLegendBehavior.tag = 'wj-flex-chart-legend';
    WjFlexChartLegendBehavior.parentProp = 'legend';
    WjFlexChartLegendBehavior.parentInCtor = true;
    WjFlexChartLegendBehavior.props = [
        'wjProperty',
        'orientation',
        'position',
        'reverse',
        'title',
        'titleAlign',
        'maxSize',
        'scrollbar',
        'isVertical'
    ];
    WjFlexChartLegendBehavior.events = [
        'initialized'
    ];
    WjFlexChartLegendBehavior.classCtor = function () { return wjcChart.Legend; };
    return WjFlexChartLegendBehavior;
}(WjComponentBehavior));
/**
 * Vue component for the {@link wijmo.chart.Legend} class.
 *
 * The <b>wj-flex-chart-legend</b> component should be contained in
 * one of the following components:
 * {@link wijmo.vue2.chart.WjFlexChart}
 * , {@link wijmo.vue2.chart.WjFlexPie}
 * , {@link wijmo.vue2.chart.finance.WjFinancialChart}
 * , {@link wijmo.vue2.chart.radar.WjFlexRadar}
 * , {@link wijmo.vue2.chart.hierarchical.WjSunburst}
 *  or {@link wijmo.vue2.chart.map.WjFlexMap}.
 *
 * The component supports all properties and events of the pure JavaScript {@link wijmo.chart.Legend} class it represents.
 *
 * The component includes an <b>initialized</b> event that is raised when the control is initialized after it is added to the page.
 * You can use this event to perform further initialization in addition to setting properties in markup.
 * The signature of the handler function is the same as any other Wijmo event handlers.
 */
export var WjFlexChartLegend = WjFlexChartLegendBehavior.register();
function registerV3WjFlexChartLegend(app) {
    app.component(WjFlexChartLegendBehavior.tag, WjFlexChartLegend);
}
var WjFlexChartDataLabelBehavior = /** @class */ (function (_super) {
    __extends(WjFlexChartDataLabelBehavior, _super);
    function WjFlexChartDataLabelBehavior() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    WjFlexChartDataLabelBehavior.tag = 'wj-flex-chart-data-label';
    WjFlexChartDataLabelBehavior.parentProp = 'dataLabel';
    WjFlexChartDataLabelBehavior.props = [
        'wjProperty',
        'content',
        'border',
        'offset',
        'connectingLine',
        'position'
    ];
    WjFlexChartDataLabelBehavior.events = [
        'initialized',
        'rendering'
    ];
    WjFlexChartDataLabelBehavior.classCtor = function () { return wjcChart.DataLabel; };
    return WjFlexChartDataLabelBehavior;
}(WjComponentBehavior));
/**
 * Vue component for the {@link wijmo.chart.DataLabel} class.
 *
 * The <b>wj-flex-chart-data-label</b> component should be contained in
 * a {@link wijmo.vue2.chart.WjFlexChart} component.
 *
 * The component supports all properties and events of the pure JavaScript {@link wijmo.chart.DataLabel} class it represents.
 *
 * The component includes an <b>initialized</b> event that is raised when the control is initialized after it is added to the page.
 * You can use this event to perform further initialization in addition to setting properties in markup.
 * The signature of the handler function is the same as any other Wijmo event handlers.
 */
export var WjFlexChartDataLabel = WjFlexChartDataLabelBehavior.register();
function registerV3WjFlexChartDataLabel(app) {
    app.component(WjFlexChartDataLabelBehavior.tag, WjFlexChartDataLabel);
}
var WjFlexPieDataLabelBehavior = /** @class */ (function (_super) {
    __extends(WjFlexPieDataLabelBehavior, _super);
    function WjFlexPieDataLabelBehavior() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    WjFlexPieDataLabelBehavior.tag = 'wj-flex-pie-data-label';
    WjFlexPieDataLabelBehavior.parentProp = 'dataLabel';
    WjFlexPieDataLabelBehavior.props = [
        'wjProperty',
        'content',
        'border',
        'offset',
        'connectingLine',
        'position'
    ];
    WjFlexPieDataLabelBehavior.events = [
        'initialized',
        'rendering'
    ];
    WjFlexPieDataLabelBehavior.classCtor = function () { return wjcChart.PieDataLabel; };
    return WjFlexPieDataLabelBehavior;
}(WjComponentBehavior));
/**
 * Vue component for the {@link wijmo.chart.PieDataLabel} class.
 *
 * The <b>wj-flex-pie-data-label</b> component should be contained in
 * a {@link wijmo.vue2.chart.WjFlexPie} component.
 *
 * The component supports all properties and events of the pure JavaScript {@link wijmo.chart.PieDataLabel} class it represents.
 *
 * The component includes an <b>initialized</b> event that is raised when the control is initialized after it is added to the page.
 * You can use this event to perform further initialization in addition to setting properties in markup.
 * The signature of the handler function is the same as any other Wijmo event handlers.
 */
export var WjFlexPieDataLabel = WjFlexPieDataLabelBehavior.register();
function registerV3WjFlexPieDataLabel(app) {
    app.component(WjFlexPieDataLabelBehavior.tag, WjFlexPieDataLabel);
}
var WjFlexChartSeriesBehavior = /** @class */ (function (_super) {
    __extends(WjFlexChartSeriesBehavior, _super);
    function WjFlexChartSeriesBehavior() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    WjFlexChartSeriesBehavior.tag = 'wj-flex-chart-series';
    WjFlexChartSeriesBehavior.parentProp = 'series';
    WjFlexChartSeriesBehavior.siblingId = 'series';
    WjFlexChartSeriesBehavior.props = [
        'wjProperty',
        'axisX',
        'axisY',
        'binding',
        'bindingX',
        'cssClass',
        'name',
        'altStyle',
        'symbolMarker',
        'symbolSize',
        'symbolStyle',
        'visibility',
        'itemsSource',
        'interpolateNulls',
        'tooltipContent',
        'itemFormatter',
        'chartType'
    ];
    WjFlexChartSeriesBehavior.events = [
        'initialized',
        'rendering',
        'rendered'
    ];
    WjFlexChartSeriesBehavior.changeEvents = {
        'chart.seriesVisibilityChanged': ['visibility'],
    };
    WjFlexChartSeriesBehavior.classCtor = function () { return wjcChart.Series; };
    return WjFlexChartSeriesBehavior;
}(WjComponentBehavior));
/**
 * Vue component for the {@link wijmo.chart.Series} class.
 *
 * The <b>wj-flex-chart-series</b> component should be contained in
 * a {@link wijmo.vue2.chart.WjFlexChart} component.
 *
 * The <b>wj-flex-chart-series</b> component may contain
 * a {@link wijmo.vue2.chart.WjFlexChartAxis} child component.
 *
 * The component supports all properties and events of the pure JavaScript {@link wijmo.chart.Series} class it represents.
 *
 * The component includes an <b>initialized</b> event that is raised when the control is initialized after it is added to the page.
 * You can use this event to perform further initialization in addition to setting properties in markup.
 * The signature of the handler function is the same as any other Wijmo event handlers.
 */
export var WjFlexChartSeries = WjFlexChartSeriesBehavior.register();
function registerV3WjFlexChartSeries(app) {
    app.component(WjFlexChartSeriesBehavior.tag, WjFlexChartSeries);
}
var WjFlexChartLineMarkerBehavior = /** @class */ (function (_super) {
    __extends(WjFlexChartLineMarkerBehavior, _super);
    function WjFlexChartLineMarkerBehavior() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    WjFlexChartLineMarkerBehavior.tag = 'wj-flex-chart-line-marker';
    WjFlexChartLineMarkerBehavior.parentInCtor = true;
    WjFlexChartLineMarkerBehavior.props = [
        'isVisible',
        'seriesIndex',
        'horizontalPosition',
        'content',
        'verticalPosition',
        'alignment',
        'lines',
        'interaction',
        'dragLines',
        'dragThreshold',
        'dragContent'
    ];
    WjFlexChartLineMarkerBehavior.events = [
        'initialized',
        'positionChanged'
    ];
    WjFlexChartLineMarkerBehavior.classCtor = function () { return wjcChart.LineMarker; };
    return WjFlexChartLineMarkerBehavior;
}(WjComponentBehavior));
/**
 * Vue component for the {@link wijmo.chart.LineMarker} class.
 *
 * The <b>wj-flex-chart-line-marker</b> component should be contained in
 * one of the following components:
 * {@link wijmo.vue2.chart.WjFlexChart}
 *  or {@link wijmo.vue2.chart.finance.WjFinancialChart}.
 *
 * The component supports all properties and events of the pure JavaScript {@link wijmo.chart.LineMarker} class it represents.
 *
 * The component includes an <b>initialized</b> event that is raised when the control is initialized after it is added to the page.
 * You can use this event to perform further initialization in addition to setting properties in markup.
 * The signature of the handler function is the same as any other Wijmo event handlers.
 */
export var WjFlexChartLineMarker = WjFlexChartLineMarkerBehavior.register();
function registerV3WjFlexChartLineMarker(app) {
    app.component(WjFlexChartLineMarkerBehavior.tag, WjFlexChartLineMarker);
}
var WjFlexChartDataPointBehavior = /** @class */ (function (_super) {
    __extends(WjFlexChartDataPointBehavior, _super);
    function WjFlexChartDataPointBehavior() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    WjFlexChartDataPointBehavior.tag = 'wj-flex-chart-data-point';
    WjFlexChartDataPointBehavior.parentProp = '';
    WjFlexChartDataPointBehavior.props = [
        'wjProperty',
        'x',
        'y'
    ];
    WjFlexChartDataPointBehavior.events = [
        'initialized'
    ];
    WjFlexChartDataPointBehavior.classCtor = function () { return wjcChart.DataPoint; };
    return WjFlexChartDataPointBehavior;
}(WjComponentBehavior));
/**
 * Vue component for the {@link wijmo.chart.DataPoint} class.
 *
 * The <b>wj-flex-chart-data-point</b> component should be contained in
 * one of the following components:
 * {@link wijmo.vue2.chart.annotation.WjFlexChartAnnotationText}
 * , {@link wijmo.vue2.chart.annotation.WjFlexChartAnnotationEllipse}
 * , {@link wijmo.vue2.chart.annotation.WjFlexChartAnnotationRectangle}
 * , {@link wijmo.vue2.chart.annotation.WjFlexChartAnnotationLine}
 * , {@link wijmo.vue2.chart.annotation.WjFlexChartAnnotationPolygon}
 * , {@link wijmo.vue2.chart.annotation.WjFlexChartAnnotationCircle}
 * , {@link wijmo.vue2.chart.annotation.WjFlexChartAnnotationSquare}
 *  or {@link wijmo.vue2.chart.annotation.WjFlexChartAnnotationImage}.
 *
 * The component supports all properties and events of the pure JavaScript {@link wijmo.chart.DataPoint} class it represents.
 *
 * The component includes an <b>initialized</b> event that is raised when the control is initialized after it is added to the page.
 * You can use this event to perform further initialization in addition to setting properties in markup.
 * The signature of the handler function is the same as any other Wijmo event handlers.
 */
export var WjFlexChartDataPoint = WjFlexChartDataPointBehavior.register();
function registerV3WjFlexChartDataPoint(app) {
    app.component(WjFlexChartDataPointBehavior.tag, WjFlexChartDataPoint);
}
var WjFlexChartPlotAreaBehavior = /** @class */ (function (_super) {
    __extends(WjFlexChartPlotAreaBehavior, _super);
    function WjFlexChartPlotAreaBehavior() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    WjFlexChartPlotAreaBehavior.tag = 'wj-flex-chart-plot-area';
    WjFlexChartPlotAreaBehavior.parentProp = 'plotAreas';
    WjFlexChartPlotAreaBehavior.props = [
        'wjProperty',
        'column',
        'height',
        'name',
        'row',
        'width'
    ];
    WjFlexChartPlotAreaBehavior.events = [
        'initialized'
    ];
    WjFlexChartPlotAreaBehavior.classCtor = function () { return wjcChart.PlotArea; };
    return WjFlexChartPlotAreaBehavior;
}(WjComponentBehavior));
/**
 * Vue component for the {@link wijmo.chart.PlotArea} class.
 *
 * The <b>wj-flex-chart-plot-area</b> component should be contained in
 * one of the following components:
 * {@link wijmo.vue2.chart.WjFlexChart}
 *  or {@link wijmo.vue2.chart.finance.WjFinancialChart}.
 *
 * The component supports all properties and events of the pure JavaScript {@link wijmo.chart.PlotArea} class it represents.
 *
 * The component includes an <b>initialized</b> event that is raised when the control is initialized after it is added to the page.
 * You can use this event to perform further initialization in addition to setting properties in markup.
 * The signature of the handler function is the same as any other Wijmo event handlers.
 */
export var WjFlexChartPlotArea = WjFlexChartPlotAreaBehavior.register();
function registerV3WjFlexChartPlotArea(app) {
    app.component(WjFlexChartPlotAreaBehavior.tag, WjFlexChartPlotArea);
}
export function registerChart(app) {
    if (VueApi.isV3Plus) {
        registerV3WjFlexChart(app);
        registerV3WjFlexPie(app);
        registerV3WjFlexChartAxis(app);
        registerV3WjFlexChartLegend(app);
        registerV3WjFlexChartDataLabel(app);
        registerV3WjFlexPieDataLabel(app);
        registerV3WjFlexChartSeries(app);
        registerV3WjFlexChartLineMarker(app);
        registerV3WjFlexChartDataPoint(app);
        registerV3WjFlexChartPlotArea(app);
    }
}
